class FescoFitmentSearch {
      constructor(element) {
        this.element = element;
        this.form = element.querySelector('form');
        this.result = element.querySelector('[data-fitment-result]');
        this.summary = element.querySelector('[data-fitment-summary]');
        this.list = element.querySelector('[data-fitment-list]');
        this.emptyMessage = element.querySelector('[data-fitment-empty]');
        this.type = element.dataset.fitmentType;
        this.importMode = new URLSearchParams(window.location.search).get('position') === 'rear' ? 'rear' : 'front';
        this.modeButtons = [...element.querySelectorAll('[data-import-mode]')];
        this.modeButtons.forEach((button) => button.addEventListener('click', () => {
          this.importMode = button.dataset.importMode;
          this.updateModeButtons();
          this.showWiperStage('maker');
        }));
        this.updateModeButtons();
        this.cascadeFields = [...element.querySelectorAll('[data-cascade-field]')];
        this.wizard = element.querySelector('[data-wiper-wizard]');
        this.wizardOptions = element.querySelector('[data-wiper-options]');
        this.wizardHeading = element.querySelector('[data-wiper-step-heading]');
        this.wizardStepLabel = element.querySelector('[data-wiper-step-label]');
        this.wizardSelection = element.querySelector('[data-wiper-selection]');
        this.wizardBack = element.querySelector('[data-wiper-back]');
        this.wizardRestart = element.querySelector('[data-wiper-restart]');
        this.noteModal = element.querySelector('[data-fitment-note-modal]');
        this.noteModalLabel = element.querySelector('[data-fitment-note-label]');
        this.noteModalText = element.querySelector('[data-fitment-note-text]');
        this.noteModalTrigger = null;
        this.wizardState = {};
        this.wizardStage = 'maker';
        this.records = [];
        this.dataUrl = new URL(element.dataset.dataUrl, window.location.href).href;
        this.assetBaseUrl = new URL('.', this.dataUrl).href;
        this.form?.addEventListener('submit', this.handleSubmit.bind(this));
        this.form?.addEventListener('reset', () => window.setTimeout(() => {
          this.resetResults();
          this.updateCascadeOptions();
        }, 0));
        this.cascadeFields.forEach((field) => field.addEventListener('change', () => this.handleCascadeChange(field)));
        this.wizardOptions?.addEventListener('click', this.handleWiperOption.bind(this));
        this.wizardBack?.addEventListener('click', this.goBackWiper.bind(this));
        this.wizardRestart?.addEventListener('click', () => {
          this.showWiperStage('maker');
          this.moveWiperViewToTop();
        });
        this.noteModal?.querySelectorAll('[data-fitment-note-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeNoteModal());
        });
        this.noteModal?.addEventListener('click', (event) => {
          if (event.target === this.noteModal) this.closeNoteModal();
        });
        this.noteModal?.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeNoteModal();
        });
        this.loadData();
      }

      noteMarkers(note) {
        const markers = String(note || '').match(/注[123]/g) || [];
        if (String(note || '').includes('※')) markers.push('注4');
        if (this.normalize(note).includes('+f-t10')) markers.push('注5');
        return [...new Set(markers)];
      }

      isWiperType() {
        return this.type === 'wiper' || this.type === 'import-wiper';
      }

      isCenterSeatFitment(fitment) {
        return String(fitment.position || '').includes('中央席');
      }

      moveWiperViewToTop() {
        if (!this.wizard || !this.wizardHeading) return;
        this.wizardHeading.focus({ preventScroll: true });
        window.requestAnimationFrame(() => {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.wizard.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        });
      }

      openNoteModal(marker, note, trigger) {
        if (!this.noteModal) return;
        this.noteModalTrigger = trigger;
        this.noteModalLabel.textContent = marker;
        this.noteModalText.replaceChildren();
        if (this.type === 'import-wiper' && marker === '注1') {
          this.noteModalText.textContent = '純正より短いサイズになっています。';
        } else if (this.type === 'import-wiper' && marker === '注2') {
          this.noteModalText.textContent = '助手席側湾曲ですので若干拭きもれが発生する恐れがあります。';
        } else if (marker === '注4') {
          this.noteModalText.textContent = 'フロントガラス付近に運転支援用カメラ・センサーを搭載した車両には使用できません。';
        } else if (marker === '注5') {
          this.noteModalText.append('ワイパー用アダプタクリップ F-T10が必要です。');
          const productLink = document.createElement('a');
          productLink.href = '/products/f-t10';
          productLink.textContent = 'F-T10の商品ページを見る';
          this.noteModalText.append(document.createElement('br'), productLink);
        } else {
          this.noteModalText.textContent = String(note || '').replace(/^適合表記[：:]\s*/, '');
        }
        this.noteModal.hidden = false;
        document.body.classList.add('fesco-fitment-modal-open');
        this.noteModal.querySelector('[data-fitment-note-close]')?.focus();
      }

      closeNoteModal() {
        if (!this.noteModal || this.noteModal.hidden) return;
        this.noteModal.hidden = true;
        document.body.classList.remove('fesco-fitment-modal-open');
        this.noteModalTrigger?.focus();
        this.noteModalTrigger = null;
      }

      async loadData() {
        try {
          const response = await window.fetch(this.dataUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Fitment data could not be loaded.');
          const data = await response.json();
          this.records = Array.isArray(data.records) ? data.records : [];
          if (this.type === 'import-wiper') {
            const rearResponse = await window.fetch(this.element.dataset.rearDataUrl, { credentials: 'same-origin' });
            if (!rearResponse.ok) throw new Error('MR data could not be loaded.');
            const rearData = await rearResponse.json();
            this.records.push(...rearData.records);
          }
          if (this.wizard) this.showWiperStage('maker');
          else this.updateCascadeOptions();
        } catch (error) {
          this.records = [];
        }

        this.emptyMessage.hidden = this.records.length > 0;
      }

      normalize(value) {
        return String(value ?? '').normalize('NFKC').trim().toLowerCase();
      }

      updateModeButtons() {
        this.modeButtons.forEach((button) => {
          const selected = button.dataset.importMode === this.importMode;
          button.setAttribute('aria-pressed', String(selected));
          button.classList.toggle('button--secondary', !selected);
        });
      }

      equals(recordValue, selectedValue) {
        return this.normalize(recordValue) === this.normalize(selectedValue);
      }

      uniqueValues(records, fieldName, split = false) {
        const values = records.flatMap((record) => split
          ? this.splitValues(record[fieldName], fieldName)
          : [String(record[fieldName] ?? '').trim()]
        ).filter(Boolean);
        return [...new Set(values)];
      }

      makerValues() {
        const domesticOrder = ['トヨタ', 'レクサス', 'ニッサン', 'ホンダ', 'マツダ', 'ミツビシ', 'スバル', 'ダイハツ', 'スズキ', 'いすゞ', '日野', 'UDトラックス', '三菱ふそう'];
        const importOrder = [
          // 欧州車：ドイツ → イタリア → イギリス → その他
          'アウディ', 'BMW', 'オペル', 'スマート（MCC)', 'フォルクスワーゲン', 'ポルシェ', 'メルセデスベンツ',
          'アバルト', 'アルファロメオ', 'フィアット', 'ランチャ',
          'ジャガー', 'ミニ(BMW)', 'ミニ', 'ベントレー', 'ランドローバー', 'ローバー',
          'サーブ', 'シトロエン', 'プジョー', 'ボルボ', 'ルノー',
          // 米国車
          'キャデラック', 'GMC', 'クライスラー', 'シボレー', 'ジープ', 'ダッジ', 'ハマー', 'フォード', 'リンカーン',
        ];
        const order = this.type === 'import-wiper' ? importOrder : domesticOrder;
        return this.uniqueValues(this.searchRecords(), 'maker').sort((a, b) => {
          const aIndex = order.indexOf(a);
          const bIndex = order.indexOf(b);
          if (aIndex === -1 && bIndex === -1) return a.localeCompare(b, 'ja');
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      }

      searchRecords() {
        if (this.type === 'import-wiper') {
          return this.records.filter((record) => (record.position === 'リア') === (this.importMode === 'rear'));
        }
        if (this.type === 'oil') {
          return this.records.filter((record) =>
            this.equals(record.category, 'オイルフィルター')
            || this.equals(record.category, 'エアコンフィルター')
          );
        }
        return this.records;
      }

      wiperRecordsForState(keys) {
        return this.searchRecords().filter((record) => keys.every((key) => {
          if (key === 'vehicle_code') {
            return this.splitValues(record[key], key).some((value) => this.equals(value, this.wizardState[key]));
          }
          return this.equals(record[key], this.wizardState[key]);
        }));
      }

      showWiperStage(stage) {
        this.wizardStage = stage;
        if (stage === 'maker') {
          this.wizardState = {};
          this.pendingWiperRecords = [];
        }
        if (stage !== 'result') this.resetResults();

        const config = {
          maker: { step: 1, label: 'STEP 1', heading: 'メーカーを選択してください' },
          model: { step: 2, label: 'STEP 2', heading: this.type === 'import-wiper' ? '車種を選択してください' : '車種名を選択してください' },
          vehicle: { step: 3, label: 'STEP 3', heading: '車両型式を選択してください' },
          year: { step: 3, label: 'STEP 3', heading: '年式・適用期間を選択してください' },
          agreement: { step: 4, label: 'STEP 4', heading: '適合情報の注意事項をご確認ください' },
          result: { step: 5, label: 'STEP 5', heading: '検索結果' },
        }[stage];

        this.wizardStepLabel.textContent = config.label;
        this.wizardHeading.textContent = config.heading;
        this.wizardBack.hidden = stage === 'maker';
        this.wizardRestart.hidden = stage === 'maker';
        this.updateWiperProgress(config.step);
        this.updateWiperSelection();
        this.wizardOptions.replaceChildren();

        let values = [];
        if (stage === 'maker') values = this.makerValues();
        if (stage === 'model') {
          values = this.uniqueValues(this.wiperRecordsForState(['maker']), 'model').sort((a, b) => a.localeCompare(b, 'ja'));
        }
        if (stage === 'year') {
          values = this.uniqueValues(this.wiperRecordsForState(['maker', 'model']), 'application_period');
        }
        if (stage === 'vehicle') {
          const modelRecords = this.wiperRecordsForState(['maker', 'model']);
          values = this.uniqueValues(modelRecords, 'vehicle_code', true)
            .sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }));
          if (this.type === 'oil' && !values.length && modelRecords.some((record) => !String(record.vehicle_code || '').trim())) {
            values = ['型式情報なし'];
          }
        }
        if (stage === 'agreement') this.renderWiperAgreement();
        else if (stage !== 'result') this.renderWiperOptions(stage, values);
      }

      renderWiperOptions(stage, values) {
        if (!values.length) {
          const message = document.createElement('p');
          message.className = 'fesco-wiper-wizard__no-options';
          message.textContent = '選択できる候補がありません。前のステップに戻って条件を変更してください。';
          this.wizardOptions.append(message);
          return;
        }
        values.forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'fesco-wiper-wizard__option';
          button.dataset.wiperStage = stage;
          button.dataset.wiperValue = value;
          const label = document.createElement('span');
          label.textContent = value;
          const arrow = document.createElement('span');
          arrow.className = 'fesco-wiper-wizard__option-arrow';
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = '→';
          button.append(label, arrow);
          this.wizardOptions.append(button);
        });
      }

      renderWiperAgreement() {
        const agreement = document.createElement('div');
        agreement.className = 'fesco-wiper-agreement';
        const heading = document.createElement('h3');
        heading.textContent = '車種別適合検索をご利用の皆様へ';
        const introduction = document.createElement('p');
        introduction.textContent = '検索結果を表示する前に、以下の内容をご確認ください。';
        const list = document.createElement('ul');
        const agreementItems = this.type === 'oil'
          ? [
            '選択したメーカー、車種名、車両型式に誤りがないことをご確認ください。',
            '同じ車両型式でも、年式・エンジン型式によって適合品番が異なる場合があります。車検証などで実車情報をご確認ください。',
            '検索結果に表示された年式・エンジン型式と実車情報が一致していることをご確認ください。',
            '適合情報に掲載されていない車両については、類似車種などの情報だけで製品を選択しないでください。',
            '適合について判断できない場合は、交換前にお問い合わせください。',
          ]
          : this.type === 'import-wiper'
            ? [
              '選択したメーカー、車種、年式・適用期間に誤りがないことをご確認ください。',
              '車種、モデル、代表型式、年式の情報は参考情報です。必ず装着車両の取付形状と長さをご確認ください。',
              this.importMode === 'rear'
                ? 'リア用の適合情報です。使用アダプタと装着品の長さを必ずご確認ください。'
                : '注1は純正より短いサイズ、注2は助手席側湾曲により若干拭きもれが発生する可能性を示します。',
              '車両の仕様変更や取付形状により、検索結果と実際の適合が異なる場合があります。',
              '適合について判断できない場合は、取り付け前にお問い合わせください。',
            ]
            : [
            '選択したメーカー、車種名、年式・適用期間に誤りがないことをご確認ください。検索結果は車両型式ごとに表示されます。',
            '車両の仕様変更や取付形状により、検索結果と実際の適合が異なる場合があります。現車の取付形状と現在使用している製品をご確認ください。',
            '適合情報に掲載されていない車両については、類似車種などの情報だけで製品を選択しないでください。',
            '交換作業の前に、商品パッケージに記載された説明と注意事項をご確認ください。',
            '適合について判断できない場合は、取り付け前にお問い合わせください。',
          ];
        agreementItems.forEach((text) => {
          const item = document.createElement('li');
          item.textContent = text;
          list.append(item);
        });

        const consentLabel = document.createElement('label');
        consentLabel.className = 'fesco-wiper-agreement__consent';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = 'fitment_agreement';
        const consentText = document.createElement('span');
        consentText.textContent = '上記の注意事項を確認し、同意します。';
        consentLabel.append(checkbox, consentText);

        const submit = document.createElement('button');
        submit.type = 'button';
        submit.className = 'button fesco-wiper-agreement__submit';
        submit.textContent = '同意して検索結果を表示';
        submit.disabled = true;
        checkbox.addEventListener('change', () => { submit.disabled = !checkbox.checked; });
        submit.addEventListener('click', () => {
          if (!checkbox.checked) return;
          this.showWiperStage('result');
          this.renderResults(this.pendingWiperRecords || []);
          this.moveWiperViewToTop();
        });
        agreement.append(heading, introduction, list, consentLabel, submit);
        this.wizardOptions.append(agreement);
      }

      handleWiperOption(event) {
        const button = event.target.closest('[data-wiper-value]');
        if (!button) return;
        const value = button.dataset.wiperValue;
        if (this.wizardStage === 'maker') {
          this.wizardState = { maker: value };
          this.showWiperStage('model');
        } else if (this.wizardStage === 'model') {
          this.wizardState.model = value;
          delete this.wizardState.application_period;
          delete this.wizardState.vehicle_code;
          this.showWiperStage(this.type === 'oil' ? 'vehicle' : 'year');
        } else if (this.wizardStage === 'vehicle') {
          this.wizardState.vehicle_code = value;
          this.pendingWiperRecords = value === '型式情報なし'
            ? this.wiperRecordsForState(['maker', 'model']).filter((record) => !String(record.vehicle_code || '').trim())
            : this.wiperRecordsForState(['maker', 'model', 'vehicle_code']);
          this.showWiperStage('agreement');
        } else if (this.wizardStage === 'year') {
          this.wizardState.application_period = value;
          this.pendingWiperRecords = this.wiperRecordsForState(['maker', 'model', 'application_period']);
          this.showWiperStage('agreement');
        }
        this.moveWiperViewToTop();
      }

      goBackWiper() {
        const previous = {
          model: 'maker',
          vehicle: 'model',
          year: 'model',
          agreement: this.type === 'oil' ? 'vehicle' : 'year',
          result: 'agreement',
        }[this.wizardStage];
        if (!previous) return;
        if (this.wizardStage === 'model') this.wizardState = {};
        if (this.wizardStage === 'year' || this.wizardStage === 'vehicle') {
          delete this.wizardState.model;
          delete this.wizardState.application_period;
          delete this.wizardState.vehicle_code;
        }
        if (this.wizardStage === 'agreement') {
          if (this.type === 'oil') delete this.wizardState.vehicle_code;
          else delete this.wizardState.application_period;
          this.pendingWiperRecords = [];
        }
        this.showWiperStage(previous);
        this.moveWiperViewToTop();
      }

      updateWiperProgress(currentStep) {
        this.element.querySelectorAll('[data-wiper-progress]').forEach((item) => {
          const step = Number(item.dataset.wiperProgress);
          item.classList.toggle('is-current', step === currentStep);
          item.classList.toggle('is-complete', step < currentStep);
          if (step === currentStep) item.setAttribute('aria-current', 'step');
          else item.removeAttribute('aria-current');
        });
      }

      updateWiperSelection() {
        const finalSelection = this.type === 'oil'
          ? this.wizardState.vehicle_code
          : this.wizardState.application_period;
        const labels = [this.wizardState.maker, this.wizardState.model, finalSelection].filter(Boolean);
        this.wizardSelection.hidden = labels.length === 0;
        this.wizardSelection.textContent = labels.length ? `選択中：${labels.join(' / ')}` : '';
      }

      handleCascadeChange(changedField) {
        const index = this.cascadeFields.indexOf(changedField);
        this.cascadeFields.slice(index + 1).forEach((field) => { field.value = ''; });
        this.updateCascadeOptions();
      }

      updateCascadeOptions() {
        this.cascadeFields.forEach((field, index) => {
          const previous = this.cascadeFields.slice(0, index);
          const previousComplete = previous.every((item) => item.value);
          const selectedValue = field.value;
          const matchingRecords = this.records.filter((record) =>
            previous.every((item) => this.matches(record[item.name], item.value))
          );
          const values = [...new Set(matchingRecords.flatMap((record) => this.splitValues(record[field.name], field.name)))].sort((a, b) =>
            String(a).localeCompare(String(b), 'ja')
          );

          field.replaceChildren();
          const prompt = document.createElement('option');
          prompt.value = '';
          prompt.textContent = index === 0 ? 'メーカーを選択' : previousComplete ? 'すべて' : `先に${this.fieldLabel(previous[index - 1]?.name)}を選択`;
          field.append(prompt);
          values.forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            field.append(option);
          });
          field.disabled = index > 0 && !previousComplete;
          if (values.includes(selectedValue)) field.value = selectedValue;
        });
      }

      splitValues(value, fieldName) {
        if (!value) return [];
        const delimiter = fieldName === 'vehicle_code' ? /[,、・]/ : /[,、]/;
        return String(value).split(delimiter).map((part) => part.trim()).filter(Boolean);
      }

      fieldLabel(name) {
        return { maker: 'メーカー', model: '車種名', vehicle_code: '車両型式', application_period: '年式' }[name] || '前の項目';
      }

      handleSubmit(event) {
        event.preventDefault();
        const conditions = Object.fromEntries(
          [...new FormData(this.form).entries()].filter(([, value]) => String(value).trim() !== '')
        );

        if (!Object.keys(conditions).length) {
          this.showMessage('検索条件を1つ以上入力してください。');
          return;
        }

        if (!this.records.length) {
          this.showMessage('適合データは現在準備中です。お急ぎの場合はお問い合わせください。');
          return;
        }

        const matches = this.records.filter((record) =>
          Object.entries(conditions).every(([key, value]) => this.matches(record[key], value))
        );
        this.renderResults(matches);
      }

      matches(recordValue, queryValue) {
        if (recordValue === undefined || recordValue === null) return false;
        return this.normalize(recordValue).includes(this.normalize(queryValue));
      }

      renderResults(records) {
        this.list.replaceChildren();
        this.result.hidden = false;
        const resultItems = this.isWiperType()
          ? this.groupWiperRecords(records)
          : this.type === 'oil'
            ? this.groupOilRecords(records)
            : records;
        this.summary.textContent = resultItems.length
          ? `${resultItems.length}件の車両適合候補が見つかりました。`
          : '条件に一致する適合情報が見つかりませんでした。条件を変えるか、お問い合わせください。';

        resultItems.forEach((record) => {
          const card = this.isWiperType()
            ? this.createWiperResultCard(record)
            : this.type === 'oil'
              ? this.createOilResultCard(record)
              : this.createResultCard(record);
          this.list.append(card);
        });
        this.result.focus();
      }

      groupWiperRecords(records) {
        const groups = new Map();
        records.forEach((record) => {
          const vehicleCodes = this.type === 'import-wiper'
            ? [record.vehicle_code]
            : this.splitValues(record.vehicle_code, 'vehicle_code');
          (vehicleCodes.length ? vehicleCodes : [record.vehicle_code]).forEach((vehicleCode) => {
            const key = [
              record.maker,
              record.model,
              record.vehicle_model,
              vehicleCode,
              record.application_period,
              record.adapter,
            ].join('\u0000');
            if (!groups.has(key)) {
              groups.set(key, {
                maker: record.maker,
                model: record.model,
                vehicle_model: record.vehicle_model,
                vehicle_code: vehicleCode,
                application_period: record.application_period,
                adapter: record.adapter,
                fitments: [],
              });
            }
            groups.get(key).fitments.push(record);
          });
        });
        return [...groups.values()];
      }

      groupOilRecords(records) {
        const groups = new Map();
        records.forEach((record) => {
          const vehicleCode = this.wizardState.vehicle_code || record.vehicle_code;
          const key = [record.maker, record.model, vehicleCode, record.engine_code, record.application_period].join('\u0000');
          if (!groups.has(key)) {
            groups.set(key, {
              maker: record.maker,
              model: record.model,
              vehicle_code: vehicleCode,
              engine_code: record.engine_code,
              application_period: record.application_period,
              fitments: [],
            });
          }
          groups.get(key).fitments.push(record);
        });
        return [...groups.values()];
      }

      createWiperResultCard(vehicle) {
        if (this.type === 'import-wiper' && this.importMode === 'rear') return this.createRearResultCard(vehicle);
        const article = document.createElement('article');
        article.className = 'fesco-fitment-vehicle';
        const heading = document.createElement('h3');
        heading.textContent = `${vehicle.maker} ${vehicle.model}`;
        article.append(heading);

        const meta = document.createElement('dl');
        meta.className = 'fesco-fitment-vehicle__meta';
        const metaFields = this.type === 'import-wiper'
          ? [
            ['モデル', vehicle.vehicle_model],
            ['代表型式', vehicle.vehicle_code],
            ['年式・適用期間', vehicle.application_period],
          ]
          : [['車両型式', vehicle.vehicle_code], ['初年度登録年月', vehicle.application_period]];
        metaFields.forEach(([label, value]) => {
          const term = document.createElement('dt');
          term.textContent = label;
          const description = document.createElement('dd');
          description.textContent = value || '―';
          meta.append(term, description);
        });
        article.append(meta);

        const positions = [
          { label: '運転席', matches: (position) => position.includes('運転席') || position.includes('左右') },
          { label: '助手席', matches: (position) => position.includes('助手席') || position.includes('中央席') || position.includes('左右') },
          { label: 'リヤ', matches: (position) => position.includes('リア') || position.includes('リヤ') },
        ];
        if (this.type === 'import-wiper') positions.pop();
        const productTypes = [...new Set(vehicle.fitments.map((fitment) => fitment.product_type).filter(Boolean))];
        const scrollHint = document.createElement('p');
        scrollHint.className = 'fesco-fitment-table-hint';
        scrollHint.textContent = '表は横にスクロールして確認できます。';
        const scroll = document.createElement('div');
        scroll.className = 'fesco-fitment-table-scroll';
        const table = document.createElement('table');
        table.className = 'fesco-fitment-wiper-table';
        const head = document.createElement('thead');
        const headRow = document.createElement('tr');
        const headerLabels = ['ワイパー種別', ...positions.map((position) => position.label)];
        if (this.type === 'import-wiper') headerLabels.push('使用アダプタ');
        headerLabels.forEach((label) => {
          const cell = document.createElement('th');
          cell.scope = 'col';
          cell.textContent = label;
          headRow.append(cell);
        });
        head.append(headRow);
        table.append(head);

        const body = document.createElement('tbody');
        productTypes.forEach((productType) => {
          const row = document.createElement('tr');
          const type = document.createElement('th');
          type.scope = 'row';
          type.textContent = productType;
          row.append(type);

          positions.forEach((position) => {
            const cell = document.createElement('td');
            const matchingFitments = vehicle.fitments.filter((fitment) =>
              fitment.product_type === productType && position.matches(String(fitment.position || ''))
            );
            const uniqueFitments = [...new Map(matchingFitments.map((fitment) => [
              `${fitment.product_sku}\u0000${fitment.product_handle || ''}\u0000${fitment.position || ''}`,
              fitment,
            ])).values()];

            if (uniqueFitments.length) {
              const list = document.createElement('ul');
              list.className = 'fesco-fitment-wiper-table__skus';
              const appendFitment = (fitment, centerSeatFitment = false) => {
                const item = document.createElement('li');
                item.className = 'fesco-fitment-wiper-table__sku-line';
                const sku = document.createElement('span');
                sku.className = 'fesco-fitment-wiper-table__sku';
                let skuContent = sku;
                if (fitment.product_handle) {
                  const link = document.createElement('a');
                  link.href = `/products/${encodeURIComponent(fitment.product_handle)}`;
                  link.textContent = fitment.product_sku;
                  sku.append(link);
                  skuContent = link;
                } else {
                  sku.textContent = fitment.product_sku;
                }
                if (centerSeatFitment) {
                  sku.classList.add('has-center-seat');
                  const centerSeat = document.createElement('span');
                  centerSeat.className = 'fesco-fitment-center-seat-label';
                  centerSeat.textContent = '（中央席取付）';
                  skuContent.append(centerSeat);
                }
                item.append(sku);

                const annotations = document.createElement('span');
                annotations.className = 'fesco-fitment-wiper-table__annotations';
                this.noteMarkers(fitment.note).forEach((marker) => {
                  const badge = document.createElement('button');
                  badge.type = 'button';
                  badge.className = 'fesco-fitment-note-badge';
                  badge.textContent = marker;
                  badge.setAttribute('aria-label', `${marker}の注意事項を表示`);
                  badge.addEventListener('click', () => this.openNoteModal(marker, fitment.note, badge));
                  annotations.append(badge);
                });
                if (annotations.childElementCount) item.append(annotations);
                list.append(item);
              };

              uniqueFitments.forEach((fitment) => {
                appendFitment(fitment, this.isCenterSeatFitment(fitment));
              });
              cell.append(list);
            } else {
              cell.className = 'fesco-fitment-wiper-table__empty';
              cell.textContent = '―';
            }
            row.append(cell);
          });
          if (this.type === 'import-wiper') {
            const adapter = document.createElement('td');
            adapter.className = vehicle.adapter ? 'fesco-fitment-wiper-table__adapter' : 'fesco-fitment-wiper-table__empty';
            adapter.textContent = vehicle.adapter || '―';
            row.append(adapter);
          }
          body.append(row);
        });
        table.append(body);
        scroll.append(table);
        article.append(scrollHint, scroll);
        return article;
      }

      createRearResultCard(vehicle) {
        const article = document.createElement('article');
        article.className = 'fesco-fitment-vehicle';
        const heading = document.createElement('h3');
        heading.textContent = `${vehicle.maker} ${vehicle.model}`;
        article.append(heading);
        vehicle.fitments.forEach((fitment) => {
          const meta = document.createElement('dl');
          meta.className = 'fesco-fitment-vehicle__meta';
          [['年式・適用期間', vehicle.application_period], ['リア用適合品番', fitment.product_sku],
            ['サイズ', `${fitment.size_mm}mm`], ['使用アダプタ', fitment.adapter]].forEach(([label, value]) => {
            const term = document.createElement('dt');
            const description = document.createElement('dd');
            term.textContent = label;
            description.textContent = value;
            meta.append(term, description);
          });
          article.append(meta);
          if (fitment.note) {
            const warning = document.createElement('p');
            warning.className = 'fesco-fitment__notice';
            warning.textContent = fitment.note;
            article.append(warning);
          }
          const actions = document.createElement('div');
          actions.className = 'fesco-fitment__mode';
          [['このサイズの商品を見る', `/products/mr?variant=${fitment.variant_id}`],
            ['取付手順・動画を見る', '/pages/mr-attachment']].forEach(([label, href], index) => {
            const link = document.createElement('a');
            link.className = index ? 'button button--secondary' : 'button';
            link.textContent = label;
            link.href = href;
            actions.append(link);
          });
          article.append(actions);
        });
        return article;
      }

      createOilResultCard(vehicle) {
        const article = document.createElement('article');
        article.className = 'fesco-fitment-vehicle fesco-fitment-oil-vehicle';
        const heading = document.createElement('h3');
        heading.textContent = `${vehicle.maker} ${vehicle.model}`;
        article.append(heading);

        const meta = document.createElement('dl');
        meta.className = 'fesco-fitment-vehicle__meta fesco-fitment-oil-vehicle__meta';
        [
          ['車両型式', vehicle.vehicle_code],
          ['エンジン型式', vehicle.engine_code],
          ['年式・適用期間', vehicle.application_period],
        ].forEach(([label, value]) => {
          const term = document.createElement('dt');
          term.textContent = label;
          const description = document.createElement('dd');
          description.textContent = value || '―';
          meta.append(term, description);
        });
        article.append(meta);

        const uniqueFitments = [...new Map(vehicle.fitments.map((fitment) => [
          `${fitment.product_sku}\u0000${fitment.product_handle || ''}`,
          fitment,
        ])).values()];
        const list = document.createElement('div');
        list.className = 'fesco-fitment-oil-products';

        uniqueFitments.forEach((fitment) => {
          const product = document.createElement('div');
          product.className = 'fesco-fitment-oil-product';
          const media = document.createElement('div');
          media.className = 'fesco-fitment-oil-product__media';
          if (fitment.product_image) {
            const image = document.createElement('img');
            image.src = fitment.product_image.startsWith('asset:')
              ? new URL(fitment.product_image.replace('asset:', ''), this.assetBaseUrl).href
              : fitment.product_image;
            image.alt = fitment.product_title || fitment.product_sku || '適合フィルター';
            image.loading = 'lazy';
            image.width = 160;
            image.height = 160;
            media.append(image);
          }

          const content = document.createElement('div');
          content.className = 'fesco-fitment-oil-product__content';
          const label = document.createElement('span');
          label.className = 'caption-with-letter-spacing';
          label.textContent = fitment.category === 'エアコンフィルター'
            ? '適合エアコンフィルター'
            : '適合オイルフィルター';
          const sku = document.createElement(fitment.product_handle ? 'a' : 'strong');
          sku.className = 'fesco-fitment-oil-product__sku';
          sku.textContent = fitment.product_sku || '品番未登録';
          if (fitment.product_handle) sku.href = `/products/${encodeURIComponent(fitment.product_handle)}`;
          content.append(label, sku);

          if (fitment.product_title) {
            const title = document.createElement('p');
            title.className = 'fesco-fitment-oil-product__title';
            title.textContent = fitment.product_title;
            content.append(title);
          }
          if (fitment.note) {
            const note = document.createElement('p');
            note.className = 'fesco-fitment-oil-product__note';
            note.textContent = fitment.note;
            content.append(note);
          }
          product.append(media, content);
          list.append(product);
        });
        article.append(list);
        return article;
      }

      createResultCard(record) {
        const article = document.createElement('article');
        article.className = 'fesco-fitment-result-card';
        const heading = document.createElement('h3');
        heading.textContent = record.product_title || record.product_sku || record.product_handle || '適合商品';
        const content = document.createElement('div');
        content.className = 'fesco-fitment-result-card__content';
        if (record.product_image) {
          const image = document.createElement('img');
          image.className = 'fesco-fitment-result-card__image';
          image.src = record.product_image.startsWith('asset:')
            ? new URL(record.product_image.replace('asset:', ''), this.assetBaseUrl).href
            : record.product_image;
          image.alt = record.product_title || record.product_sku || '適合商品';
          image.loading = 'lazy';
          image.width = 240;
          image.height = 240;
          article.append(image);
        }
        content.append(heading);

        const fields = this.type === 'wiper'
          ? [['品番', 'product_sku'], ['取付位置', 'position'], ['サイズ', 'size_mm'], ['種類', 'product_type'], ['適用期間', 'application_period'], ['注意事項', 'note']]
          : [['品番', 'product_sku'], ['カテゴリー', 'category'], ['エンジン型式', 'engine_code'], ['適用期間', 'application_period'], ['注意事項', 'note']];
        const definitionList = document.createElement('dl');
        fields.forEach(([label, key]) => {
          if (!record[key]) return;
          const term = document.createElement('dt');
          term.textContent = label;
          const description = document.createElement('dd');
          description.textContent = record[key];
          definitionList.append(term, description);
        });
        content.append(definitionList);

        if (record.product_handle) {
          const link = document.createElement('a');
          link.className = 'button button--secondary';
          link.href = `/products/${encodeURIComponent(record.product_handle)}`;
          link.textContent = '商品情報を見る';
          content.append(link);
        }
        article.append(content);
        return article;
      }

      showMessage(message) {
        this.list.replaceChildren();
        this.result.hidden = false;
        this.summary.textContent = message;
        this.result.focus();
      }

      resetResults() {
        this.result.hidden = true;
        this.list.replaceChildren();
      }
}

document.querySelectorAll('fesco-fitment-search').forEach((element) => {
  if (!element.fescoFitmentSearch) element.fescoFitmentSearch = new FescoFitmentSearch(element);
});
